/**
 * TanStack Query hooks for Brain Server API.
 * Pattern matches useEntities.ts.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchSessions,
  fetchBrainWorkOrders,
  fetchBrainWorkOrder,
  createBrainWorkOrder,
  approveBrainWorkOrder,
  rejectBrainWorkOrder,
  fetchDailyCosts,
} from '../api/brain'
import type { WorkOrderFilters, CreateWorkOrderData } from '../types/brain'

// --- Sessions ---

export function useSessions(limit = 20) {
  return useQuery({
    queryKey: ['brain', 'sessions', limit],
    queryFn: () => fetchSessions(limit),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

// --- Work Orders ---

export function useBrainWorkOrders(filters?: WorkOrderFilters) {
  return useQuery({
    queryKey: ['brain', 'work-orders', filters],
    queryFn: () => fetchBrainWorkOrders(filters),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

export function useBrainWorkOrder(id: string | null) {
  return useQuery({
    queryKey: ['brain', 'work-orders', id],
    queryFn: () => fetchBrainWorkOrder(id!),
    enabled: !!id,
    staleTime: 15_000,
  })
}

export function useCreateBrainWorkOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateWorkOrderData) => createBrainWorkOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brain', 'work-orders'] })
    },
  })
}

export function useApproveBrainWorkOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      approveBrainWorkOrder(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brain', 'work-orders'] })
    },
  })
}

export function useRejectBrainWorkOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      rejectBrainWorkOrder(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brain', 'work-orders'] })
    },
  })
}

// --- Costs ---

export function useDailyCosts() {
  return useQuery({
    queryKey: ['brain', 'costs', 'daily'],
    queryFn: fetchDailyCosts,
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
}
